import React, { useState, useEffect, useContext, useCallback } from 'react';
import { OrganizationContext } from '../contexts/OrganizationContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  MessageSquare, Mic, Settings, Palette, FileText, Shield,
  Code, Loader2, CheckCircle2, AlertCircle, Globe, BarChart2,
  RefreshCw, Copy, ChevronDown, ChevronRight
} from 'lucide-react';
import {
  getChatbotConfig,
  saveChatbotConfig,
  scanWebsite,
  getChatHistory,
  getChatStats,
  getPublishedVAPIAssistants,
} from '../services/api';


// ── Section wrapper ───────────────────────────────────────────────────────────

const Section = ({ icon: Icon, title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50
                   hover:bg-slate-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2 font-medium text-slate-700 text-sm">
          <Icon className="w-4 h-4 text-slate-500" />
          {title}
        </div>
        {open
          ? <ChevronDown className="w-4 h-4 text-slate-400" />
          : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="px-4 py-4 space-y-4">{children}</div>}
    </div>
  );
};

// ── Field helpers ─────────────────────────────────────────────────────────────

const Field = ({ label, hint, children }) => (
  <div className="space-y-1">
    <Label className="text-sm font-medium">{label}</Label>
    {hint && <p className="text-xs text-slate-400">{hint}</p>}
    {children}
  </div>
);

const ToggleRow = ({ label, description, checked, onChange, disabled = false }) => (
  <div className="flex items-center justify-between rounded-lg border p-3">
    <div>
      <p className="text-sm font-medium">{label}</p>
      {description && <p className="text-xs text-slate-400 mt-0.5">{description}</p>}
    </div>
    <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
  </div>
);


// ── Main component ────────────────────────────────────────────────────────────

const ChatbotConfigurator = () => {
  const { organizationId } = useContext(OrganizationContext);

  const [config, setConfig] = useState({
    assistantId:        '',
    assistantName:      '',
    chatMode:           'streaming',
    sessionBased:       false,
    widgetMode:         'chat',
    widgetSize:         'full',
    widgetPosition:     'bottom-right',
    theme:              'light',
    radius:             'medium',
    baseColor:          '',
    accentColor:        '#14B8A6',
    buttonBaseColor:    '#000000',
    buttonAccentColor:  '#FFFFFF',
    mainLabel:          'Chat with Us',
    startButtonText:    'Start',
    endButtonText:      'End Call',
    emptyChatMessage:   'Hi! How can I help you today?',
    emptyVoiceMessage:  'Click to start a voice conversation',
    requireConsent:     false,
    termsContent:       '',
    localStorageKey:    'vapi_widget_consent',
    showTranscript:     true,
  });

  const [assistants, setAssistants]     = useState([]);
  const [websiteUrl, setWebsiteUrl]     = useState('');
  const [scanning, setScanning]         = useState(false);
  const [scannedColors, setScannedColors] = useState([]);
  const [isSaving, setIsSaving]         = useState(false);
  const [isLoading, setIsLoading]       = useState(true);
  const [message, setMessage]           = useState({ type: '', content: '' });
  const [activeTab, setActiveTab]       = useState('configure');
  const [chatHistory, setChatHistory]   = useState([]);
  const [stats, setStats]               = useState(null);
  const [copied, setCopied]             = useState(false);

  // ── Data loading ────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    if (!organizationId) return;
    try {
      setIsLoading(true);
      const [cfg, assts] = await Promise.all([
        getChatbotConfig(organizationId),
        getPublishedVAPIAssistants(organizationId),
      ]);
      if (cfg) setConfig(prev => ({ ...prev, ...cfg }));
      setAssistants(assts || []);
    } catch (e) {
      setMessage({ type: 'error', content: 'Failed to load configuration.' });
    } finally {
      setIsLoading(false);
    }
  }, [organizationId]);

  useEffect(() => { load(); }, [load]);

  const loadHistory = useCallback(async () => {
    if (!organizationId) return;
    try {
      const [hist, st] = await Promise.all([
        getChatHistory(organizationId, 0, 20),
        getChatStats(organizationId),
      ]);
      setChatHistory(hist?.content || []);
      setStats(st);
    } catch (e) {
      console.error('Failed to load chat history:', e);
    }
  }, [organizationId]);

  useEffect(() => {
    if (activeTab === 'history') loadHistory();
  }, [activeTab, loadHistory]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    setMessage({ type: '', content: '' });
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setMessage({ type: '', content: '' });
      const saved = await saveChatbotConfig(organizationId, config);
      setConfig(prev => ({ ...prev, ...saved }));
      setMessage({ type: 'success', content: 'Chatbot configuration saved.' });
    } catch (e) {
      setMessage({ type: 'error', content: e.message || 'Save failed.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleScanWebsite = async () => {
    if (!websiteUrl) return;
    try {
      setScanning(true);
      setMessage({ type: '', content: '' });
      const result = await scanWebsite(organizationId, websiteUrl);
      const colors = (result?.scanned?.colors || '').split(',').filter(Boolean);
      setScannedColors(colors);
      if (result?.suggested) {
        setConfig(prev => ({ ...prev, ...result.suggested }));
        setMessage({ type: 'success', content: 'Website scanned — suggested colours applied below. Review and save.' });
      }
    } catch (e) {
      setMessage({ type: 'error', content: 'Could not scan website. Check the URL and try again.' });
    } finally {
      setScanning(false);
    }
  };

  const handleCopyScript = () => {
    const script = generateWidgetScript();
    navigator.clipboard.writeText(script).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // ── Widget script generation ─────────────────────────────────────────────────

  const generateWidgetScript = () => {
    const attrs = [
      `  public-key="YOUR_VAPI_PUBLIC_KEY"`,
      `  assistant-id="${config.assistantId || 'YOUR_ASSISTANT_ID'}"`,
      `  mode="${config.widgetMode}"`,
      `  theme="${config.theme}"`,
      `  size="${config.widgetSize}"`,
      `  position="${config.widgetPosition}"`,
      `  radius="${config.radius}"`,
      config.baseColor           ? `  base-color="${config.baseColor}"` : null,
      `  accent-color="${config.accentColor}"`,
      `  button-base-color="${config.buttonBaseColor}"`,
      `  button-accent-color="${config.buttonAccentColor}"`,
      `  main-label="${config.mainLabel}"`,
      `  start-button-text="${config.startButtonText}"`,
      `  end-button-text="${config.endButtonText}"`,
      config.emptyChatMessage    ? `  empty-chat-message="${config.emptyChatMessage}"` : null,
      config.emptyVoiceMessage   ? `  empty-voice-message="${config.emptyVoiceMessage}"` : null,
      config.requireConsent      ? `  require-consent="true"` : null,
      config.requireConsent && config.termsContent
                                 ? `  terms-content="${config.termsContent.replace(/"/g, '&quot;')}"` : null,
      config.requireConsent      ? `  local-storage-key="${config.localStorageKey}"` : null,
      !config.showTranscript     ? `  show-transcript="false"` : null,
    ].filter(Boolean).join('\n');

    return `<!-- Vapi AI Chat Widget -->\n<script src="https://unpkg.com/@vapi-ai/client-sdk-react/dist/embed/widget.umd.js" async type="text/javascript"></script>\n\n<vapi-widget\n${attrs}\n></vapi-widget>`;
  };

  const generateReactScript = () => {
    return `import { VapiWidget } from '@vapi-ai/client-sdk-react';

function ChatWidget() {
  return (
    <VapiWidget
      publicKey="YOUR_VAPI_PUBLIC_KEY"
      assistantId="${config.assistantId || 'YOUR_ASSISTANT_ID'}"
      mode="${config.widgetMode}"
      theme="${config.theme}"
      size="${config.widgetSize}"
      position="${config.widgetPosition}"
      accentColor="${config.accentColor}"
      mainLabel="${config.mainLabel}"
      ${config.requireConsent ? `requireConsent={true}` : ''}
      onCallStart={() => console.log('Voice started')}
      onCallEnd={() => console.log('Voice ended')}
      onMessage={(msg) => console.log('Message:', msg)}
      onError={(err) => console.error('Widget error:', err)}
    />
  );
}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4">

      {/* Page header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">AI Chatbot</h1>
          <p className="text-sm text-slate-500 mt-1">
            Configure your organisation's AI chatbot and generate the embed code
            for your website.
          </p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'configure' && (
            <Button onClick={handleSave} disabled={isSaving} size="sm">
              {isSaving
                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Saving…</>
                : 'Save Configuration'}
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b pb-3">
        {[
          { id: 'configure', label: 'Configure',    icon: Settings    },
          { id: 'widget',    label: 'Widget Code',  icon: Code        },
          { id: 'history',   label: 'Chat History', icon: MessageSquare },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm
                        transition-colors ${activeTab === tab.id
                          ? 'bg-slate-900 text-white'
                          : 'text-slate-600 hover:bg-slate-100'}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Status message */}
      {message.content && (
        <Alert className={message.type === 'error'
          ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}>
          {message.type === 'error'
            ? <AlertCircle className="h-4 w-4 text-red-600" />
            : <CheckCircle2 className="h-4 w-4 text-green-600" />}
          <AlertDescription className={message.type === 'error'
            ? 'text-red-700' : 'text-green-700'}>
            {message.content}
          </AlertDescription>
        </Alert>
      )}

      {/* ══ CONFIGURE TAB ═══════════════════════════════════════════════════ */}
      {activeTab === 'configure' && (
        <div className="space-y-3">

          {/* Assistant */}
          <Section icon={Settings} title="Assistant">
            <Field label="Vapi Assistant" hint="Select the assistant to power your chatbot.">
              <Select
                value={config.assistantId}
                onValueChange={v => {
                  const a = assistants.find(x => x.id === v || x.assistantId === v);
                  handleChange('assistantId', v);
                  handleChange('assistantName', a?.name || '');
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an assistant…" />
                </SelectTrigger>
                <SelectContent>
                  {assistants.map(a => (
                    <SelectItem key={a.id || a.assistantId} value={a.id || a.assistantId}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Chat Mode">
                <Select value={config.chatMode} onValueChange={v => handleChange('chatMode', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="streaming">Streaming (real-time)</SelectItem>
                    <SelectItem value="non-streaming">Non-streaming</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Session Type">
                <ToggleRow
                  label="Session-based"
                  description="Continue conversations using previousChatId"
                  checked={config.sessionBased}
                  onChange={v => handleChange('sessionBased', v)}
                />
              </Field>
            </div>
          </Section>

          {/* Widget mode & layout */}
          <Section icon={MessageSquare} title="Widget Mode & Layout">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Mode">
                <Select value={config.widgetMode} onValueChange={v => handleChange('widgetMode', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="chat">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-3.5 h-3.5" /> Chat (text)
                      </div>
                    </SelectItem>
                    <SelectItem value="voice">
                      <div className="flex items-center gap-2">
                        <Mic className="w-3.5 h-3.5" /> Voice
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Size">
                <Select value={config.widgetSize} onValueChange={v => handleChange('widgetSize', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">Full</SelectItem>
                    <SelectItem value="compact">Compact</SelectItem>
                    <SelectItem value="tiny">Tiny</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Position">
                <Select value={config.widgetPosition} onValueChange={v => handleChange('widgetPosition', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bottom-right">Bottom Right</SelectItem>
                    <SelectItem value="bottom-left">Bottom Left</SelectItem>
                    <SelectItem value="top-right">Top Right</SelectItem>
                    <SelectItem value="top-left">Top Left</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Theme">
                <Select value={config.theme} onValueChange={v => handleChange('theme', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">Light</SelectItem>
                    <SelectItem value="dark">Dark</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Border Radius">
                <Select value={config.radius} onValueChange={v => handleChange('radius', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="small">Small</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="large">Large</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>

            {config.widgetMode === 'voice' && (
              <Alert className="bg-amber-50 border-amber-200">
                <Mic className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-700 text-xs">
                  Voice mode requires microphone access and HTTPS on the target website.
                  Users will be prompted to allow microphone access on first use.
                </AlertDescription>
              </Alert>
            )}
          </Section>

          {/* Styling */}
          <Section icon={Palette} title="Styling — Match Your Website">
            {/* Website scanner */}
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 space-y-2">
              <p className="text-xs font-medium text-blue-800 flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5" /> Auto-scan website colours
              </p>
              <div className="flex gap-2">
                <Input
                  value={websiteUrl}
                  onChange={e => setWebsiteUrl(e.target.value)}
                  placeholder="https://your-website.com"
                  className="text-sm"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleScanWebsite}
                  disabled={scanning || !websiteUrl}
                  className="shrink-0"
                >
                  {scanning
                    ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Scanning…</>
                    : 'Scan'}
                </Button>
              </div>
              {scannedColors.length > 0 && (
                <div>
                  <p className="text-xs text-blue-700 mb-1">Detected colours — click to use:</p>
                  <div className="flex gap-1.5 flex-wrap">
                    {scannedColors.map(color => (
                      <button
                        key={color}
                        onClick={() => handleChange('accentColor', color)}
                        title={color}
                        className="w-6 h-6 rounded border border-white shadow-sm"
                        style={{ background: color }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Base Background Colour">
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={config.baseColor || '#ffffff'}
                    onChange={e => handleChange('baseColor', e.target.value)}
                    className="w-12 h-9 p-1 cursor-pointer"
                  />
                  <Input
                    value={config.baseColor}
                    onChange={e => handleChange('baseColor', e.target.value)}
                    placeholder="#ffffff"
                    className="font-mono text-sm"
                  />
                </div>
              </Field>
              <Field label="Accent Colour">
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={config.accentColor}
                    onChange={e => handleChange('accentColor', e.target.value)}
                    className="w-12 h-9 p-1 cursor-pointer"
                  />
                  <Input
                    value={config.accentColor}
                    onChange={e => handleChange('accentColor', e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
              </Field>
              <Field label="Button Background">
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={config.buttonBaseColor}
                    onChange={e => handleChange('buttonBaseColor', e.target.value)}
                    className="w-12 h-9 p-1 cursor-pointer"
                  />
                  <Input
                    value={config.buttonBaseColor}
                    onChange={e => handleChange('buttonBaseColor', e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
              </Field>
              <Field label="Button Icon Colour">
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={config.buttonAccentColor}
                    onChange={e => handleChange('buttonAccentColor', e.target.value)}
                    className="w-12 h-9 p-1 cursor-pointer"
                  />
                  <Input
                    value={config.buttonAccentColor}
                    onChange={e => handleChange('buttonAccentColor', e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
              </Field>
            </div>
          </Section>

          {/* Labels */}
          <Section icon={FileText} title="Labels & Messages" defaultOpen={false}>
            <div className="grid grid-cols-2 gap-3">
              {[
                ['mainLabel',         'Widget Header',         'Chat with Us'],
                ['startButtonText',   'Start Button',          'Start'],
                ['endButtonText',     'End Button',            'End Call'],
                ['emptyChatMessage',  'Chat Welcome Message',  'Hi! How can I help?'],
                ['emptyVoiceMessage', 'Voice Welcome Message', 'Click to start voice'],
              ].map(([field, label, placeholder]) => (
                <Field key={field} label={label}>
                  <Input
                    value={config[field]}
                    placeholder={placeholder}
                    onChange={e => handleChange(field, e.target.value)}
                  />
                </Field>
              ))}
            </div>
          </Section>

          {/* Consent */}
          <Section icon={Shield} title="Consent Management" defaultOpen={false}>
            <ToggleRow
              label="Require Consent"
              description="Show a consent form before the first message. Required for GDPR compliance."
              checked={config.requireConsent}
              onChange={v => handleChange('requireConsent', v)}
            />
            {config.requireConsent && (
              <>
                <Field label="Consent Text" hint="Displayed to users before they start chatting.">
                  <Textarea
                    value={config.termsContent}
                    rows={3}
                    placeholder="By using this chat widget, you agree to our privacy policy..."
                    onChange={e => handleChange('termsContent', e.target.value)}
                  />
                </Field>
                <Field label="Local Storage Key" hint="Key used to remember consent across sessions.">
                  <Input
                    value={config.localStorageKey}
                    onChange={e => handleChange('localStorageKey', e.target.value)}
                    className="font-mono text-sm"
                  />
                </Field>
              </>
            )}
            <ToggleRow
              label="Show Transcript"
              description="Display the voice conversation transcript in the widget."
              checked={config.showTranscript}
              onChange={v => handleChange('showTranscript', v)}
            />
          </Section>

        </div>
      )}

      {/* ══ WIDGET CODE TAB ═════════════════════════════════════════════════ */}
      {activeTab === 'widget' && (
        <div className="space-y-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
            Replace <code className="font-mono bg-amber-100 px-1 rounded">YOUR_VAPI_PUBLIC_KEY</code> with
            your Vapi <strong>public</strong> key (safe to expose in browser code — not your private key).
          </div>

          {/* HTML embed */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center justify-between">
                <span>HTML Embed (any website)</span>
                <Button variant="outline" size="sm" onClick={handleCopyScript}>
                  {copied
                    ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1 text-green-600" />Copied!</>
                    : <><Copy className="w-3.5 h-3.5 mr-1" />Copy</>}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="bg-slate-900 text-green-400 p-4 rounded-lg text-xs overflow-x-auto
                             font-mono leading-relaxed whitespace-pre">
                {generateWidgetScript()}
              </pre>
            </CardContent>
          </Card>

          {/* React embed */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">React Component</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="bg-slate-900 text-blue-300 p-4 rounded-lg text-xs overflow-x-auto
                             font-mono leading-relaxed whitespace-pre">
                {generateReactScript()}
              </pre>
              <p className="text-xs text-slate-400 mt-2">
                Install: <code className="font-mono bg-slate-100 px-1 rounded">
                  npm install @vapi-ai/client-sdk-react
                </code>
              </p>
            </CardContent>
          </Card>

          {/* Live preview note */}
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-xs text-slate-500">
            <strong className="text-slate-700">To preview:</strong> copy the HTML embed code and
            paste it into a local HTML file served over HTTPS (or localhost). The widget requires
            a valid Vapi public key to initialise.
          </div>
        </div>
      )}

      {/* ══ CHAT HISTORY TAB ════════════════════════════════════════════════ */}
      {activeTab === 'history' && (
        <div className="space-y-4">

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 gap-3">
              <Card>
                <CardContent className="pt-4">
                  <p className="text-xs text-slate-400">Total Chat Cost</p>
                  <p className="text-2xl font-bold">
                    £{Number(stats.totalCost || 0).toFixed(4)}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-xs text-slate-400">Total Tokens Used</p>
                  <p className="text-2xl font-bold">
                    {Number(stats.totalTokens || 0).toLocaleString()}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* History table */}
          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="text-sm">Recent Conversations</CardTitle>
              <Button variant="ghost" size="sm" onClick={loadHistory}>
                <RefreshCw className="w-3.5 h-3.5" />
              </Button>
            </CardHeader>
            <CardContent>
              {chatHistory.length === 0 ? (
                <p className="text-center text-slate-400 py-8 text-sm">
                  No chat conversations yet.
                </p>
              ) : (
                <div className="space-y-2">
                  {chatHistory.map(chat => (
                    <div key={chat.id}
                         className="border rounded-lg p-3 space-y-1 hover:bg-slate-50">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs text-slate-400">{chat.chatId}</span>
                        <div className="flex gap-1.5">
                          <Badge variant="outline" className="text-xs">
                            {chat.chatMode || 'chat'}
                          </Badge>
                          <Badge variant={chat.status === 'active' ? 'default' : 'secondary'}
                                 className="text-xs">
                            {chat.status}
                          </Badge>
                        </div>
                      </div>
                      {chat.lastUserMessage && (
                        <p className="text-sm text-slate-700 truncate">
                          <span className="text-slate-400">User: </span>
                          {chat.lastUserMessage}
                        </p>
                      )}
                      {chat.lastAssistantResponse && (
                        <p className="text-sm text-slate-500 truncate">
                          <span className="text-slate-400">AI: </span>
                          {chat.lastAssistantResponse}
                        </p>
                      )}
                      <div className="flex gap-3 text-xs text-slate-400">
                        {chat.totalTokens && <span>{chat.totalTokens} tokens</span>}
                        {chat.cost && <span>£{Number(chat.cost).toFixed(6)}</span>}
                        <span>{new Date(chat.createdAt).toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ChatbotConfigurator;